import { useEffect, useRef, useState } from "react";

import BrushWord from "../components/BrushWord";
import IconMount from "../components/IconMount";
import { PROUD_LEDE, STATS } from "../data/content";
import { ScrollTrigger, countUp, prefersReducedMotion, revealUp } from "../lib/motion";
import "./Proud.css";

/**
 * The three numbers the lab measures itself by.
 *
 */
export default function Proud() {
  const rootRef = useRef(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const items = [...root.querySelectorAll(".ilab-proud__item")];
    const cleanups = [
      revealUp([root.querySelector(".ilab-proud__head")], { y: 26, stagger: 0 }),
      revealUp(items, {
        y: 34,
        stagger: 0.14,
        trigger: root.querySelector(".ilab-proud__grid"),
      }),
      ...items.map((item, i) =>
        countUp(item.querySelector(".ilab-proud__value"), STATS[i].value),
      ),
    ];

    /* Draw the coloured rule alongside each number. */
    items.forEach((item) => {
      if (prefersReducedMotion()) {
        item.classList.add("is-in");
        return;
      }
      const trigger = ScrollTrigger.create({
        trigger: item,
        start: "top 88%",
        once: true,
        onEnter: () => item.classList.add("is-in"),
      });
      cleanups.push(() => trigger.kill());
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <section className="section ilab-proud" ref={rootRef} aria-labelledby="proud-title">
      <div className="container">
        <header className="ilab-proud__head">
          <h2 className="section-title" id="proud-title">
            We are <BrushWord variant="section">Proud</BrushWord>
          </h2>
          <p className="section-lede ilab-proud__lede">{PROUD_LEDE}</p>
        </header>

        {/* A list rather than a <dl>: the visual order puts the number above
            its label, which would mean emitting <dd> before <dt>, and a
            definition list with reversed or div-wrapped pairs is exactly what
            the axe `definition-list` rule flags. The number itself is hidden
            from assistive tech because it counts up from zero; the real value
            is announced once, next to its label. */}
        <ul className="ilab-proud__grid">
          {STATS.map(({ id, icon, value, label, tone }) => (
            <li
              className={`ilab-proud__item ilab-proud__item--${tone}`}
              key={id}
              onMouseEnter={() => setHovered(id)}
              onMouseLeave={() => setHovered(null)}
            >
              <IconMount name={icon} tone={tone} size="sm" active={hovered === id} />

              <p className="ilab-proud__value" aria-hidden="true">
                0
              </p>
              <p className="ilab-proud__label">
                <span className="sr-only">{value} </span>
                {label}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
